/**
 * Real, live projects. Every fact here is visible on the linked site or in
 * the product itself; no client metrics are claimed. Screenshots come from
 * data/workShots.json (generated by scripts/work-shots.js).
 */
export const WORK = [
  {
    slug: 'staffly',
    name: 'Staffly',
    url: 'https://stafflyapp.com/',
    domain: 'stafflyapp.com',
    sector: 'HRMS · Attendance & payroll',
    kind: 'Mobile app + web dashboard',
    year: 'Live',
    summary: 'An attendance, payroll and compliance platform for Indian businesses. Staff punch in from a phone, owners run payroll from a dashboard.',
    stat: { kpi: '10+', label: 'Indian languages supported' },
    services: ['Mobile App', 'Custom Software', 'Web App'],
    palette: ['#6C5CE7', '#0A0A0F'],
    seoTitle: 'Staffly Case Study: HRMS & Attendance App | NioDelta',
    seoDescription: 'How NioDelta built Staffly, an attendance, payroll and compliance app for Indian businesses: face attendance, GPS check-ins, bulk payroll, 10+ languages.',
    h1: 'Staffly — HRMS, Attendance & Payroll App',
    overview: [
      'Staffly replaces the attendance register, the salary spreadsheet and the payment app with one product. Owners see the whole business on a dashboard, managers get only the access they need, and staff use a free app in their own language.',
      'We designed and built the product end to end and still run it: the Flutter mobile app, the web dashboard, the backend, and the marketing site at stafflyapp.com.',
    ],
    built: [
      { k: 'AI face attendance', v: 'Any Android device becomes a touchless face-recognition terminal at the gate, so there are no cards and no buddy punching.' },
      { k: 'Selfie & GPS check-in', v: 'Geo-fenced punch-in with a timestamped selfie for field and multi-site teams.' },
      { k: 'Bulk payroll', v: 'Salaries, advances, overtime, fines and deductions calculated per salary type and paid out together.' },
      { k: 'Statutory compliance', v: 'PF, ESI and TDS components calculated and tracked with each payroll run.' },
      { k: 'Cashbook & reports', v: 'Cash-in, cash-out and expense tracking with reports for the accountant.' },
      { k: 'Role-based access', v: 'Separate experiences for owners, managers, staff and field teams inside one app.' },
    ],
    stack: ['Flutter (Android)', 'Web dashboard', 'Custom backend & APIs', 'On-device face recognition', 'GPS geo-fencing', '10+ Indian languages'],
    serviceLinks: [
      { to: '/services/mobile-app-development/', label: 'Mobile app development' },
      { to: '/services/custom-software-development/', label: 'Custom software development' },
      { to: '/services/flutter-app-development/', label: 'Flutter app development' },
    ],
  },
  {
    slug: 'shiksha-crm',
    name: 'Shiksha CRM',
    url: 'https://shikshacrm.in/',
    domain: 'shikshacrm.in',
    sector: 'Education · School management',
    kind: 'Web platform + parent app',
    year: 'Live',
    summary: 'A school management system and admissions CRM for Indian schools: admissions, fees, attendance, exams, bus tracking and a parent app in one system.',
    stat: { kpi: '25+', label: 'modules in one system' },
    services: ['Custom Software', 'Web App', 'Mobile App'],
    palette: ['#4B9FFF', '#0A0A0F'],
    seoTitle: 'Shiksha CRM Case Study: School Management Software | NioDelta',
    seoDescription: 'How NioDelta built Shiksha CRM: school management software with admissions CRM, fees, attendance, exams, live GPS bus tracking and a parent mobile app.',
    h1: 'Shiksha CRM — School Management Software & Parent App',
    overview: [
      'Schools usually run on four or five disconnected tools: an enquiry register, a fee package, an attendance sheet, a bus-tracking vendor and a WhatsApp group. Shiksha CRM puts them on one record, from the first admission enquiry to the last bus home.',
      'We built the web platform for school staff, the parent mobile app, and the product website. The system is multi-branch, with role-based access and separate data for each school.',
    ],
    built: [
      { k: 'Admissions CRM', v: 'Every walk-in and phone enquiry becomes a tracked lead, and an admission converts into a full student record in one click.' },
      { k: 'Fees & finance', v: 'Fee structures, collection, receipts and dues, with automatic reminders to parents.' },
      { k: 'Attendance with alerts', v: 'Staff and student attendance that notifies parents the same day.' },
      { k: 'Live GPS bus tracking', v: 'Parents and the transport office see where each bus is in real time.' },
      { k: 'WhatsApp automation', v: 'Fee reminders, notices and results sent to parents automatically.' },
      { k: 'Exams, library & inventory', v: 'Examination schedules and results, library issue and return, and stock records.' },
    ],
    stack: ['PHP 8 backend', 'Web dashboard', 'Flutter parent app', 'GPS tracking', 'WhatsApp Business API', 'Multi-branch access control'],
    serviceLinks: [
      { to: '/services/custom-software-development/', label: 'Custom software development' },
      { to: '/services/web-app-development/', label: 'Web app development' },
      { to: '/services/whatsapp-chatbot-development/', label: 'WhatsApp chatbot and automation' },
    ],
  },
  {
    slug: 'foodysh',
    name: 'Foodysh',
    url: 'https://www.foodysh.com/',
    domain: 'foodysh.com',
    sector: 'Food delivery · Nashik',
    kind: 'Website + delivery apps',
    year: 'Live',
    summary: 'A food delivery brand built for Nashik: local kitchens, local dishes and delivery areas from Panchavati to Nashik Road.',
    stat: { kpi: '8', label: 'Nashik delivery areas' },
    services: ['Website Development', 'Mobile App', 'Ecommerce'],
    palette: ['#6C5CE7', '#4B9FFF'],
    seoTitle: 'Foodysh Case Study: Food Delivery Website, Nashik | NioDelta',
    seoDescription: 'How NioDelta built Foodysh, a Nashik food delivery brand: a fast React website with dish and restaurant browsing, live tracking flow and app download.',
    h1: 'Foodysh — Food Delivery Website & App for Nashik',
    overview: [
      'Foodysh is a food delivery service for one city. The brief was to make it feel like Nashik, not like a national app with a city filter: misal pav and pithla bhakri up front, kitchens grouped by the roads people actually name, and delivery areas listed street by street.',
      'We designed and built the public website as a fast single-page React application, with dish categories, restaurant cards, offers, a three-step ordering explainer and the app download section.',
    ],
    built: [
      { k: 'Dish-first browsing', v: 'Filter by Nashik specials, biryani, South Indian, street food, pizza and desserts, with price, rating and delivery time on every card.' },
      { k: 'Restaurant listings', v: 'Kitchen cards with cuisine, area, delivery time, hygiene badge and the current offer.' },
      { k: 'Ordering flow', v: 'Pick a dish, dispatch with live GPS tracking, delivery at the door, explained in three steps.' },
      { k: 'App download section', v: 'App Store and Google Play calls to action with an in-page phone preview.' },
      { k: 'Local coverage', v: 'Delivery areas listed by name: Panchavati, College Road, Gangapur Road, Mahatma Nagar, Indira Nagar, CIDCO, Satpur and Nashik Road.' },
      { k: 'Partner & rider entry points', v: 'Separate paths for restaurants to partner and riders to join.' },
    ],
    stack: ['React', 'Tailwind CSS', 'Single-page application', 'Mobile-first layout', 'Android & iOS apps'],
    serviceLinks: [
      { to: '/services/food-delivery-app-development/', label: 'Food delivery app development' },
      { to: '/website-development-company-nashik/', label: 'Website design company in Nashik' },
      { to: '/services/website-development/', label: 'Website development' },
    ],
  },
  {
    slug: 'rupantaram',
    name: 'Rupantaram',
    url: 'https://rupantaram.com/',
    domain: 'rupantaram.com',
    sector: 'Education · Life skills academy',
    kind: 'Marketing website',
    year: 'Live',
    summary: 'The website for a life skills and personality development academy with residential camps for teenagers, young adults and parents across four centres in Maharashtra.',
    stat: { kpi: '4', label: 'training centres covered' },
    services: ['Website Development'],
    palette: ['#4B9FFF', '#6C5CE7'],
    seoTitle: 'Rupantaram Case Study: Academy Website Design | NioDelta',
    seoDescription: 'How NioDelta built the Rupantaram website: programme pages by age group, batch countdown, bilingual content and enquiry flow for a life skills academy.',
    h1: 'Rupantaram — Website for a Life Skills Academy',
    overview: [
      'Rupantaram runs residential personality development and life skills camps at Karjat, Alibag, Pen and Lonavala. Parents are the buyers, and they need to trust the academy before they send a child away for eleven days.',
      'We built a content-rich site that answers a parent’s questions in order: what is taught, which programme fits which age, how a day is structured, who the mentors are, and what other families say. Each programme has its own page, and the next batch date is always on screen.',
    ],
    built: [
      { k: 'Programme pages by age', v: 'Separate pages for Youth Transform, Young Adult Transform, Parenting 360° and the 11-day residential programme.' },
      { k: 'Next-batch countdown', v: 'A live countdown and register button for the upcoming batch in the header of every page.' },
      { k: 'Life skills curriculum', v: 'The full curriculum laid out skill by skill, grouped under discipline, character and readiness for the future.' },
      { k: 'Bilingual content', v: 'English and Marathi versions with a language switch, and Devanagari type set properly.' },
      { k: 'Trust pages', v: 'Team, success stories, gallery, FAQ for parents, and clear refund, privacy and terms pages.' },
      { k: 'Enquiry paths', v: 'Call, WhatsApp and “talk to a mentor” actions on every page, plus a contact form per centre.' },
    ],
    stack: ['React', 'Multi-page routing', 'English + Marathi', 'SEO metadata per page', 'Mobile-first layout'],
    serviceLinks: [
      { to: '/services/website-development/', label: 'Website development' },
      { to: '/website-development-company-mumbai/', label: 'Web design company in Mumbai' },
      { to: '/website-development-cost/', label: 'Website development cost' },
    ],
  },
  {
    slug: 'forexpips',
    name: 'ForexPips',
    url: 'https://forexpips.in/',
    domain: 'forexpips.in',
    sector: 'Fintech · Algorithmic trading',
    kind: 'Website + client portal',
    year: 'Live',
    summary: 'The website and client portal for a gold and forex algorithmic trading service that connects to clients’ MetaTrader 5 accounts.',
    stat: { kpi: 'MT5', label: 'trading platform integration' },
    services: ['Web App', 'Website Development', 'Custom Software'],
    palette: ['#6C5CE7', '#0A0A0F'],
    seoTitle: 'ForexPips Case Study: Trading Platform Web App | NioDelta',
    seoDescription: 'How NioDelta built the ForexPips website and client portal: live market ticker, package selection, MT5 account onboarding and client login.',
    h1: 'ForexPips — Website & Client Portal for Algo Trading',
    overview: [
      'ForexPips offers automated gold and forex trading strategies that run on a client’s own MetaTrader 5 account. The product needed a public site that explains the service plainly and a secure area where clients sign in, pick a package and connect an account.',
      'We built both: the marketing site with a live market ticker and chart, and the PHP application behind the login that handles registration, packages, account linking and support.',
    ],
    built: [
      { k: 'Live market strip', v: 'Streaming gold and currency prices and a live chart on the home page.' },
      { k: 'Client registration & login', v: 'Sign-up, sign-in and a client area for managing the service.' },
      { k: 'MT5 account onboarding', v: 'A three-step flow: connect the MetaTrader 5 account, choose a package, start.' },
      { k: 'Package management', v: 'Plans shown to signed-in clients only, managed from the back office.' },
      { k: 'Education & reports', v: 'An education centre and performance report sections for clients.' },
      { k: 'Support & contact', v: 'Contact form and support entry points wired to the team.' },
    ],
    stack: ['PHP application', 'Tailwind CSS', 'MetaTrader 5 integration', 'TradingView market data', 'Client authentication'],
    serviceLinks: [
      { to: '/services/web-app-development/', label: 'Web app development' },
      { to: '/services/custom-software-development/', label: 'Custom software development' },
      { to: '/software-development-company-mumbai/', label: 'Software development company in Mumbai' },
    ],
    note: 'NioDelta built the software. Trading involves risk; nothing on this page is investment advice.',
  },
];

export const workPath = (slug) => `/work/${slug}`;
export const workByPath = (path) => WORK.find((w) => workPath(w.slug) === path);
